<template>
  <b-container fluid>
    <b-card no-body>
      <b-card-header>
        <b-card-title>Inventarisatiestaat exporteren</b-card-title>
      </b-card-header>
      <b-card-body>
        <b-row>
          <!-- Kolom 1: Filters -->
          <b-col md="3">
            <b-form-group label="Relatie">
              <b-select
                v-model="relatie"
                :options="relaties"
                :state="relatie != null"
                text-field="Naam"
                value-field="Id"
              >
                <template #first>
                  <b-form-select-option :value="null" disabled>
                    -- Kies een van de {{ relaties.length }} relaties --
                  </b-form-select-option>
                </template>
              </b-select>
            </b-form-group>

            <b-form-group label="Voorselectie velden">
              <b-button
                v-for="preset in presets"
                :key="preset.label"
                variant="outline-primary"
                size="sm"
                class="mr-1 mb-1"
                @click="selected = preset.value"
              >
                {{ preset.label }}
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mr-1 mb-1"
                @click="selectAll"
              >
                Alles
              </b-button>
              <b-button
                variant="outline-secondary"
                size="sm"
                class="mb-1"
                @click="selected = []"
              >
                Deselecteer
              </b-button>
            </b-form-group>

            <b-form-group
              label="Certificaat"
              description="Filter op geldigheid van het certificaat"
            >
              <b-form-checkbox-group
                v-model="certificaat"
                :options="[
                  { text: 'Ja', value: 1 },
                  { text: 'Nee', value: 0 },
                  { text: 'Verlopen', value: -3 },
                  { text: 'NVT', value: -2 },
                  { text: 'Onbekend', value: -1 },
                ]"
                stacked
              />
            </b-form-group>

            <b-form-group label="Beheer">
              <b-form-radio-group
                id="btn-radios-2"
                v-model="status"
                :options="[
                  { text: 'In beheer', value: true },
                  { text: 'Niet in beheer', value: false },
                ]"
                buttons
                button-variant="outline-primary"
                size="sm"
                name="radio-btn-outline"
              />
            </b-form-group>
          </b-col>

          <!-- Kolom 2: Veldkeuze -->
          <b-col md="4">
            <b-form-group label="Te exporteren velden">
              <b-form-input
                v-model="fieldSearch"
                placeholder="Zoek velden..."
                size="sm"
                class="mb-2"
              />
              <div
                v-for="group in filteredGroupedCols"
                :key="group.label"
              >
                <div class="text-muted small font-weight-bold text-uppercase mt-2 mb-1">
                  {{ group.label }}
                </div>
                <b-form-checkbox
                  v-for="col in group.cols"
                  :key="col.value"
                  v-model="selected"
                  :value="col.value"
                >
                  {{ col.text }}
                  <b-badge v-if="col.warning" variant="warning" class="ml-1">intern</b-badge>
                </b-form-checkbox>
              </div>
            </b-form-group>
          </b-col>

          <!-- Kolom 3: Volgorde & Exporteren -->
          <b-col md="5">
            <small class="text-muted d-block mb-1">Volgorde van de te exporteren gegevens</small>
            <draggable
              v-model="selected"
              handle=".drag-handle"
              ghost-class="drag-ghost"
              :animation="150"
            >
              <b-list-group-item
                v-for="(item, index) in selectie"
                :key="item.code"
                class="d-flex align-items-center py-1 px-2"
              >
                <i class="fa fa-bars drag-handle mr-2 text-muted" />
                <span class="text-muted small mr-2">{{ index + 1 }}</span>
                <span class="flex-grow-1">{{ item.naam }}</span>
                <b-badge variant="secondary" class="ml-1">{{ item.code }}</b-badge>
              </b-list-group-item>
            </draggable>

            <b-form-group
              description="De MJOP zal achteraan de velden worden geplaatst."
              class="mt-3"
            >
              <b-input-group>
                <b-input-group-prepend is-text>
                  Aantal jaren MJOP
                </b-input-group-prepend>
                <b-form-input
                  v-model.number="jaren"
                  type="range"
                  min="0"
                  max="60"
                  step="10"
                  class="range-input"
                />
                <b-input-group-append is-text>
                  {{ jaren == 0 ? 'geen MJOP' : `${jaren} jaren` }}
                </b-input-group-append>
              </b-input-group>
            </b-form-group>

            <b-alert :show="interneInfoWaarschuwing" variant="warning">
              De export bevat informatie die alleen voor intern gebruik is!
            </b-alert>

            <b-alert
              :show="!!exportError"
              variant="danger"
              dismissible
              @dismissed="exportError = null"
            >
              {{ exportError }}
            </b-alert>

            <b-input-group>
              <b-input-group-prepend is-text>
                Bestandsnaam
              </b-input-group-prepend>
              <b-input v-model="downloadNaam" />
              <b-input-group-append>
                <b-button
                  :disabled="!relatie || selected.length == 0 || isbusy"
                  variant="success"
                  @click="exportInventarisatie"
                >
                  <b-spinner v-if="isbusy" small class="mr-1" />
                  <i v-else class="fa fa-table mr-1" />
                  {{ isbusy ? 'Exporteren...' : 'Exporteer excel' }}
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>
  </b-container>
</template>
<script>
import { serviceMixin } from "@/mixins/serviceMixin";
import { saveAs } from "file-saver";
import draggable from "vuedraggable";

const preset1 = [
  "unitkenmerk",
  "titel",
  "onderhoudsnummer",
  "laatsteinspectiedatum",
  "keuringsinstantie",
  "keuringsnummer",
  "adres",
  "wijkplaats",
  "regio",
  "bouwjaar",
  "modrevisiejaar",
  "leverancier",
  "onderhoudsfirmanaam",
  "fabrieksnummer",
  "onderhoudscategorie",
  "beheerstatus",
  "actueleomschrijving",
  "keuringsDeadlineAlsRelevant",
  "datumlaatstekeuring",
  "GSMSPL",
  "CC",
  "ARBO",
  "ARPJ",
  "soortonderhoud",
  "APE",
];

const preset2 = [
  "unitkenmerk", "titel", "adres", "wijkplaats", "leverancier",
  "fabrieksnummer", "onderhoudsfirmanaam", "onderhoudsnummer",
  "keuringsinstantie", "keuringsnummer", "datumlaatstekeuring",
  "keuringsDeadlineAlsRelevant", "actueleomschrijving",
];

const preset3 = [
  "unitkenmerk", "titel", "adres", "wijkplaats", "leverancier",
  "fabrieksnummer", "bouwjaar", "HFH", "AST", "LCA", "HSN", "RTBB",
  "onderhoudsfirmanaam", "onderhoudsnummer", "keuringsinstantie",
  "keuringsnummer", "datumlaatstekeuring", "keuringsDeadlineAlsRelevant",
  "actueleomschrijving",
];

export default {
  name: "InventarisatieStaatExport",
  components: { draggable },
  mixins: [serviceMixin],
  data() {
    return {
      status: true,
      downloadNaam: "inventarisatiestaat.xlsx",
      relaties: [],
      relatie: null,
      isbusy: false,
      exportError: null,
      fieldSearch: "",
      certificaat: [-3, -2, -1, 0, 1],
      jaren: 0,
      presets: [
        { label: "Standaard", value: preset1 },
        { label: "Keuringen", value: preset2 },
        { label: "Keuringen TUV", value: preset3 },
      ],
      selected: preset1,
      cols: [
        // Identificatie
        { text: "unit nr.", value: "unitkenmerk", type: "string", group: "Identificatie" },
        { text: "titel", value: "titel", type: "string", group: "Identificatie" },
        { text: "install.nr.OH", value: "onderhoudsnummer", type: "string", group: "Identificatie" },
        { text: "adres", value: "adres", type: "string", group: "Identificatie" },
        { text: "wijk/plaats", value: "wijkplaats", type: "string", group: "Identificatie" },
        { text: "regio", value: "regio", type: "string", group: "Identificatie" },
        // Keuring
        { text: "keurings-instantie", value: "keuringsinstantie", type: "string", group: "Keuring" },
        { text: "keuringsnr.", value: "keuringsnummer", type: "string", group: "Keuring" },
        { text: "inspectiedatum", value: "laatsteinspectiedatum", type: "date", warning: true, group: "Keuring" },
        { text: "keuringsdatum", value: "datumlaatstekeuring", type: "date", group: "Keuring" },
        { text: "geldigheid certificaat", value: "keuringsDeadlineAlsRelevant", type: "date", group: "Keuring" },
        { text: "certificaat", value: "actueleomschrijving", type: "string", group: "Keuring" },
        // Onderhoud
        { text: "fabrikaat/lev.", value: "leverancier", type: "string", group: "Onderhoud" },
        { text: "onderhoudsfirma", value: "onderhoudsfirmanaam", type: "string", group: "Onderhoud" },
        { text: "fabrieksnummer", value: "fabrieksnummer", type: "string", group: "Onderhoud" },
        { text: "OH-categorie", value: "onderhoudscategorie", type: "string", group: "Onderhoud" },
        { text: "soort onderhoud", value: "soortonderhoud", type: "string", group: "Onderhoud" },
        // Algemeen
        { text: "bouwjaar", value: "bouwjaar", type: "numeric", group: "Algemeen" },
        { text: "mod.-/revisiejaar", value: "modrevisiejaar", type: "numeric", group: "Algemeen" },
        { text: "algemene conditie", value: "algemeneconditie", type: "numeric", group: "Algemeen" },
        { text: "beheer status", value: "beheerstatus", type: "string", group: "Algemeen" },
        // SPL
        { text: "telefoonnummer (SPL)", value: "GSMSPL", type: "string", group: "SPL" },
        { text: "type (SPL)", value: "CC", type: "string", group: "SPL" },
        { text: "fabrikant (SPL)", value: "CCF", type: "string", group: "SPL" },
        { text: "ARBO-veilig", value: "ARBO", type: "string", group: "SPL" },
        { text: "aantal ritten per jaar", value: "ARPJ", type: "numeric", group: "SPL" },
        { text: "vervoer personen", value: "APE", type: "numeric", group: "SPL" },
        // Technisch
        { text: "heflast (kg)", value: "LCA", type: "string", group: "Technisch" },
        { text: "hef-snelheid(m/s)", value: "HSN", type: "numeric", group: "Technisch" },
        { text: "hef-hoogte(m)", value: "HFH", type: "numeric", group: "Technisch" },
        { text: "vermogen", value: "VM", type: "string", group: "Technisch" },
        { text: "aantal cabine-toegangen", value: "ATC", type: "string", group: "Technisch" },
        { text: "type cabine-toegang", value: "TPCAB", type: "string", group: "Technisch" },
        { text: "aantal stop-plaatsen", value: "AST", type: "numeric", group: "Technisch" },
        { text: "aantal schacht-toegangen", value: "ASCH", type: "numeric", group: "Technisch" },
        { text: "type ophanging", value: "OPHA", type: "string", group: "Technisch" },
        { text: "besturing in groep", value: "BST", type: "string", group: "Technisch" },
        { text: "type besturing", value: "TYB", type: "string", group: "Technisch" },
        { text: "type regeling", value: "TYR", type: "string", group: "Technisch" },
        { text: "type aandrijving", value: "FTA", type: "string", group: "Technisch" },
        { text: "type schachttoegangen", value: "TSCH", type: "string", group: "Technisch" },
        { text: "bijzondere besturing", value: "RTBB", type: "string", group: "Technisch" },
      ],
    };
  },
  computed: {
    interneInfoWaarschuwing() {
      return this.selected.includes("laatsteinspectiedatum");
    },
    groupedCols() {
      const groups = {};
      this.cols.forEach((col) => {
        if (!groups[col.group]) groups[col.group] = [];
        groups[col.group].push(col);
      });
      return Object.entries(groups).map(([label, cols]) => ({ label, cols }));
    },
    filteredGroupedCols() {
      if (!this.fieldSearch) return this.groupedCols;
      const q = this.fieldSearch.toLowerCase();
      return this.groupedCols
        .map((group) => ({
          label: group.label,
          cols: group.cols.filter((col) => col.text.toLowerCase().includes(q)),
        }))
        .filter((group) => group.cols.length > 0);
    },
    selectie() {
      return this.selected.map((elem) => {
        const col = this.cols.find((c) => c.value === elem);
        if (!col) return { naam: elem, code: elem, type: "string" };
        return { naam: col.text, code: elem, type: col.type };
      });
    },
  },
  mounted() {
    this.getRelaties();
  },
  methods: {
    selectAll() {
      this.selected = this.cols.map((c) => c.value);
    },
    getRelaties() {
      this.get("relaties").then((response) => {
        this.relaties = response.data.relaties;
      });
    },
    exportInventarisatie() {
      this.isbusy = true;
      this.exportError = null;
      this.$http
        .post(
          "/inventarisatie",
          {
            relatieid: this.relatie,
            cols: this.selectie,
            aantaljaren: Math.max(0, this.jaren - 1),
            certificaat: this.certificaat,
            beheer: this.status ? [1, 2, 3] : [0],
          },
          { responseType: "arraybuffer" }
        )
        .then((response) => {
          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          saveAs(blob, this.downloadNaam);
        })
        .catch(() => {
          this.exportError = "Er is een fout opgetreden bij het exporteren. Probeer het opnieuw.";
        })
        .finally(() => {
          this.isbusy = false;
        });
    },
  },
};
</script>

<style scoped>
.drag-handle {
  cursor: grab;
}
.drag-handle:active {
  cursor: grabbing;
}
.drag-ghost {
  opacity: 0.4;
  background: #c8ebfb;
}
.range-input {
  padding: 0.6rem 0.75rem;
}
</style>
